import React, { useState } from "react";
import "react-tooltip/dist/react-tooltip.css";
import { RectangleStackIcon, CheckIcon } from "@heroicons/react/24/outline";
import { MultiSSOLogin } from "./MultiSSOLogin";

const WorkspaceCard = ({ workspace, isSelected = false, onClick }) => {
  const logo = workspace?.square_logo || workspace?.image_favicon || workspace?.image_logo;

  return (
    <button
      type="button"
      onClick={onClick}
      title={workspace?.name}
      className={`
        group relative flex flex-col items-center justify-center h-36 w-36 sm:h-40 sm:w-40 cursor-pointer rounded-2xl border bg-white p-3.5 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-blue-400 hover:shadow-md active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-blue-500/40
        ${isSelected ? "border-blue-500 bg-blue-50/50 ring-2 ring-blue-500/20 shadow-md" : "border-gray-200/90 hover:bg-gray-50/50"}
      `}>
      {isSelected && (
        <div className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shadow-md ring-2 ring-white">
          <CheckIcon className="h-3.5 w-3.5 stroke-[2.5]" />
        </div>
      )}

      <div className="flex h-full w-full flex-col items-center justify-center space-y-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-100/80 p-1.5 transition-transform duration-200 group-hover:scale-105 group-hover:bg-blue-50/80">
          {logo ? (
            <img
              src={logo}
              alt={workspace?.name || "Workspace"}
              className="h-full w-full object-contain rounded-lg"
            />
          ) : (
            <RectangleStackIcon className="h-6 w-6 text-gray-400 transition-colors duration-200 group-hover:text-blue-500" />
          )}
        </div>
        <div className="w-full px-1">
          <h3 className="line-clamp-2 text-center text-xs sm:text-sm font-semibold text-gray-800 transition-colors duration-200 group-hover:text-blue-600 break-words leading-tight">
            {workspace?.name}
          </h3>
        </div>
      </div>
    </button>
  );
};

const WorkspaceSelector = ({ workspaces = [], handleBackToLogin, onSSOSelect, className = "", renderPasswordOption }) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [showSSOSelection, setShowSSOSelection] = useState(false);

  const handleWorkspaceSelect = (workspace) => {
    const dualLogin = !workspace?.dual_signin_for_user && workspace?.ssos?.length === 1;

    if (dualLogin) {
      const ssoProvider = workspace.ssos[0];
      onSSOSelect?.(ssoProvider, workspace);
    } else {
      setSelectedWorkspaceId(workspace._id);
      setShowSSOSelection(true);
    }
  };

  const handleBackToWorkspaceSelect = () => {
    setShowSSOSelection(false);
    setSelectedWorkspaceId(null);
  };

  const handleSSOProviderSelect = (ssoProvider) => {
    const selectedWorkspace = workspaces.find((w) => w._id === selectedWorkspaceId);
    onSSOSelect?.(ssoProvider, selectedWorkspace);
  };

  const selectedWorkspace = workspaces.find((w) => w._id === selectedWorkspaceId);

  if (showSSOSelection && selectedWorkspace) {
    return (
      <MultiSSOLogin
        workspace={selectedWorkspace}
        sso={selectedWorkspace.ssos || []}
        onSSOSelect={handleSSOProviderSelect}
        onBack={handleBackToWorkspaceSelect}
        showBackButton={true}
        renderPasswordOption={renderPasswordOption}
      />
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="mb-6 w-full text-center space-y-2">
        {workspaces?.length > 1 && (
          <h2 className="text-center text-xl sm:text-2xl font-bold tracking-tight text-gray-900">
            Choose a workspace to login
          </h2>
        )}
        <button
          type="button"
          onClick={handleBackToLogin}
          className="mx-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors focus:outline-none">
          Return to <span className="text-highlightColor font-semibold hover:underline">Login Page</span>
        </button>
      </div>
      <div className="w-full">
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 pb-2">
          {workspaces?.length === 1 ? (
            <MultiSSOLogin
              workspace={workspaces[0]}
              sso={workspaces[0].ssos}
              onSSOSelect={(ssoProvider) => onSSOSelect?.(ssoProvider, workspaces[0])}
              showBackButton={false}
              renderPasswordOption={renderPasswordOption}
            />
          ) : (
            workspaces?.map((workspace) => (
              <WorkspaceCard
                key={workspace._id}
                workspace={workspace}
                isSelected={workspace._id === selectedWorkspaceId}
                onClick={() => handleWorkspaceSelect(workspace)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSelector;
